import React, { useEffect } from 'react';
import { ArrowLeft, ArrowUpRight, Smartphone, Users, Zap, TrendingUp, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

const HealofyCaseStudy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-pastel-bg min-h-screen font-sans text-neutral-900 selection:bg-accent/20">
      
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-pastel-bg/90 backdrop-blur-md border-b border-neutral-200/50">
        <div className="container mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-neutral-600 hover:text-accent transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">Back to Home</span>
          </Link>
        </div>
      </nav>

      <main className="pt-32 pb-20">
        <div className="container mx-auto px-6 md:px-12 max-w-4xl">
          
          {/* 1. Header */}
          <header className="mb-20">
            <h1 className="text-4xl md:text-6xl font-serif text-neutral-900 mb-6 leading-tight">
              India's Largest Vernacular Parenting App
            </h1>
            <p className="text-xl text-neutral-600 leading-relaxed max-w-3xl mb-6">
              Designing localized, vernacular-first experiences for tier 2/3 cities to empower mothers across India.
            </p>

            <a 
              href="https://play.google.com/store/apps/details?id=com.healofy&hl=en_IN" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-accent font-medium mb-10 hover:underline"
            >
              View Live Product <ArrowUpRight className="w-4 h-4" />
            </a>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white p-6 rounded-2xl border border-neutral-100">
                <h3 className="text-xs uppercase tracking-wider font-bold text-accent mb-2">Growth</h3>
                <p className="text-lg font-bold">~40 Lakh DAU</p>
                <p className="text-sm text-neutral-500">Daily Active Users</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-neutral-100">
                <h3 className="text-xs uppercase tracking-wider font-bold text-accent mb-2">Rank</h3>
                <p className="text-lg font-bold">#1 Parenting App</p>
                <p className="text-sm text-neutral-500">on Google Play Store</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-neutral-100">
                <h3 className="text-xs uppercase tracking-wider font-bold text-accent mb-2">My Role</h3>
                <p className="text-lg font-bold">UI/UX Lead</p>
                <p className="text-sm text-neutral-500">Founding Team Member</p>
              </div>
            </div>
          </header>

          {/* 2. Overview */}
          <section className="mb-24">
            <h2 className="text-3xl font-serif mb-8">Overview</h2>
            <div className="bg-white p-8 rounded-3xl border border-neutral-100">
              <p className="text-lg text-neutral-700 leading-relaxed mb-6">
                As one of the founding members of the team, I led end-to-end product design for web and mobile products, owning the entire process from discovery to delivery.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="flex items-start gap-3">
                  <Users className="w-5 h-5 text-accent mt-1" />
                  <div>
                    <h4 className="font-bold text-neutral-900">Target Audience</h4>
                    <p className="text-sm text-neutral-500">Mothers in Tier 2 & 3 cities across India</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Smartphone className="w-5 h-5 text-accent mt-1" />
                  <div>
                    <h4 className="font-bold text-neutral-900">Platform</h4>
                    <p className="text-sm text-neutral-500">Mobile First (Android & Web)</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* 3. Key Achievements */}
          <section className="mb-24">
            <h2 className="text-3xl font-serif mb-8">Key Achievements</h2>
            <div className="grid grid-cols-1 gap-6">
              {[
                { 
                  title: "Vernacular-First Design", 
                  desc: "Enabled scalable design for a vernacular-first product serving millions of users, breaking language barriers.",
                  icon: <Zap className="w-5 h-5 text-white" />
                },
                { 
                  title: "Design System", 
                  desc: "Built and evolved a scalable design system, improving consistency, speed, and quality across the platform.",
                  icon: <TrendingUp className="w-5 h-5 text-white" />
                },
                { 
                  title: "Data-Driven Decisions", 
                  desc: "Conducted qualitative and quantitative user research to uncover behavioral patterns and translate insights into actionable design.",
                  icon: <CheckCircle className="w-5 h-5 text-white" />
                }
              ].map((card, i) => (
                <div key={i} className="bg-white p-8 rounded-3xl border border-neutral-100 flex items-start gap-6 hover:shadow-sm transition-shadow">
                  <div className="w-12 h-12 bg-accent rounded-2xl flex items-center justify-center shrink-0 shadow-lg shadow-accent/20">
                    {card.icon}
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-neutral-900 mb-2">{card.title}</h3>
                    <p className="text-neutral-600 leading-relaxed">{card.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

        </div>
      </main>
    </div>
  );
};

export default HealofyCaseStudy;
